// functionRuns.js
// Read-only client for `users/{company_id}/function_runs`.
//
// History uses the company-wide recency stream without composite indexes —
//
//     orderBy('created_at', 'desc')  [+ startAfter(cursor)]  + limit(n)
//
// An equality-only function_id existence check avoids scanning an empty
// Function's history. The ordered stream is served from its automatic single-field index. A
// `where('function_id', '==', …)` combined with that ordering would require a
// composite index, so per-function history is instead produced by PAGING the
// recency stream and filtering client-side.
//
// History scans read sequential pages, retaining only matching records. They
// stop at a match (Function history) or once every card has a completed result
// (View hydration), or when the company stream is exhausted.
//
// Every returned run carries `_snap`, the Firestore document snapshot, so a
// caller can continue a scan without a second read.

import {
  collection,
  doc,
  endBefore,
  onSnapshot,
  getDoc,
  getDocsFromServer,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { runCreatedAt } from '../utils/runDisplay';

// How many runs one Activity page returns. The Activity feed reads the recency
// stream directly with no filtering, so this is a true page size.
export const ACTIVITY_PAGE_SIZE = 20;

// How many documents one batched newest-per-function window reads. ONE query
// answers "the newest run of each of these functions" for every function at
// once, which is what keeps the sidebar and view hydration to a single read.
export const LATEST_WINDOW = 80;

// A function's run history is read in BATCHES: one 200-document slice of the
// company's run stream per page, showing every run of the function in that
// slice (so a page holds however many of the function's runs fell in those 200
// documents), and the next page scans the next 200. The scan only continues
// past a slice on its own when the slice held none of the function's runs, so
// the first page is never empty while runs exist in the company history.
export const RUN_BATCH_SIZE = 200;

const runsCollection = (companyId) => collection(db, `users/${companyId}/function_runs`);

const mapRun = (runDoc) => ({
  id: runDoc.id,
  ...runDoc.data(),
  _snap: runDoc,
});

// A run has a displayable result only when its compute status reached
// `completed`; a failed run's result was never persisted.
export const isCompletedRun = (run) => run?.status === 'completed';

export const latestCompletedRun = (runs) => (
  (Array.isArray(runs) ? runs : []).find(isCompletedRun) || null
);

// Read a server-confirmed history page. A cached short page cannot establish
// that history is exhausted.
async function readStreamChunk(companyId, afterDoc, size) {
  const constraints = [orderBy('created_at', 'desc')];
  if (afterDoc) constraints.push(startAfter(afterDoc));
  constraints.push(limit(size));
  return getDocsFromServer(query(runsCollection(companyId), ...constraints));
}

// Return the matching runs in the next company-history batch. Empty batches
// are skipped, so "no runs" means the history was actually exhausted. The
// caller can abort a scan when its Function or company selection changes.
export async function loadFunctionRuns(companyId, functionId, options = {}) {
  const { cursor = null, signal } = options;
  let afterDoc = cursor?.lastSnap || null;
  let scanned = cursor?.scanned || 0;
  if (!companyId || !functionId) {
    return { runs: [], cursor: null, hasMore: false, scanned: 0 };
  }
  if (!cursor) {
    if (signal?.aborted) throw new DOMException('History read cancelled.', 'AbortError');
    const existence = await getDocsFromServer(query(runsCollection(companyId),
      where('function_id', '==', functionId), limit(1)));
    if (signal?.aborted) throw new DOMException('History read cancelled.', 'AbortError');
    if (existence.empty) return { runs: [], cursor: null, hasMore: false, scanned: 0 };
  }
  while (true) {
    if (signal?.aborted) throw new DOMException('History read cancelled.', 'AbortError');
    const snapshot = await readStreamChunk(companyId, afterDoc, RUN_BATCH_SIZE);
    if (signal?.aborted) throw new DOMException('History read cancelled.', 'AbortError');
    scanned += snapshot.docs.length;
    const runs = snapshot.docs.map(mapRun).filter(run => run.function_id === functionId);
    if (snapshot.docs.length > 0) afterDoc = snapshot.docs[snapshot.docs.length - 1];
    const hasMore = snapshot.docs.length === RUN_BATCH_SIZE;
    if (runs.length > 0 || !hasMore) {
      return { runs, cursor: { lastSnap: afterDoc, scanned }, hasMore, scanned };
    }
  }
}

// One page of every function's runs, newest first (the Activity feed). This is
// the raw recency stream, so it is neither filtered nor capped.
export async function loadAllRuns(companyId, options = {}) {
  const { pageSize = ACTIVITY_PAGE_SIZE, afterDoc = null } = options;
  if (!companyId) return { runs: [], lastDoc: null, hasMore: false };

  const snapshot = await readStreamChunk(companyId, afterDoc, pageSize);
  const runs = snapshot.docs.map(mapRun);
  return {
    runs,
    lastDoc: runs.length > 0 ? runs[runs.length - 1]._snap : afterDoc,
    hasMore: runs.length === pageSize,
  };
}

// Prefer the shared window below when asking for several Functions at once.
export async function loadLatestRun(companyId, functionId, options = {}) {
  const { runs } = await loadFunctionRuns(companyId, functionId, options);
  return runs[0] || null;
}

// The newest run — and the newest COMPLETED run — of many functions, from ONE
// read of the recency stream.
//
// This is what replaces "one latest-run query per function". Anything a single
// window cannot answer is reported in `missingLatest` / `missingCompleted` so
// the caller can decide whether that function is worth a deeper scan of
// its own, or whether an empty card and a missing status dot are acceptable.
export async function loadLatestRunsByFunction(companyId, functionIds, options = {}) {
  const { window: windowSize = LATEST_WINDOW } = options;
  const wanted = Array.from(new Set((functionIds || []).filter(Boolean)));
  const byFunction = {};
  wanted.forEach((functionId) => { byFunction[functionId] = { latest: null, latestCompleted: null }; });

  if (!companyId || wanted.length === 0) {
    return { byFunction, missingLatest: wanted, missingCompleted: wanted, scanned: 0 };
  }

  const snapshot = await readStreamChunk(companyId, null, windowSize);
  // The stream is already newest-first, so the first sighting of a function is
  // its newest run and the first completed sighting is its newest result.
  snapshot.docs.forEach((runDoc) => {
    const run = mapRun(runDoc);
    const entry = byFunction[run.function_id];
    if (!entry) return;
    if (!entry.latest) entry.latest = run;
    if (!entry.latestCompleted && isCompletedRun(run)) entry.latestCompleted = run;
  });

  return {
    byFunction,
    missingLatest: wanted.filter((functionId) => !byFunction[functionId].latest),
    missingCompleted: wanted.filter((functionId) => !byFunction[functionId].latestCompleted),
    scanned: snapshot.docs.length,
  };
}

// Find the newest completed run of each target in one sequential scan. A
// target is { key, functionId, matches? }; View cards match their own identity.
// Report relevant records as they are read so a live observer can retain runs
// that are still executing, including those outside the discovery window.
export async function loadLatestCompletedRunsFor(companyId, targets, options = {}) {
  const { window: windowSize = LATEST_WINDOW, signal, onRuns } = options;
  const wanted = (targets || []).filter(target => target?.key && target.functionId);
  const resolved = Object.fromEntries(wanted.map(target => [target.key, null]));
  if (!companyId || wanted.length === 0) return resolved;
  let afterDoc = null;
  while (wanted.some(target => !resolved[target.key])) {
    if (signal?.aborted) throw new DOMException('History read cancelled.', 'AbortError');
    const snapshot = await readStreamChunk(companyId, afterDoc, windowSize);
    if (signal?.aborted) throw new DOMException('History read cancelled.', 'AbortError');
    const relevant = [];
    for (const document of snapshot.docs) {
      const run = mapRun(document);
      const matches = wanted.filter(target => run.function_id === target.functionId
        && (!target.matches || target.matches(run)));
      if (matches.length === 0) continue;
      relevant.push(run);
      for (const target of matches) {
        if (!resolved[target.key] && isCompletedRun(run)) resolved[target.key] = run;
      }
    }
    onRuns?.(relevant);
    if (snapshot.docs.length < windowSize) break;
    afterDoc = snapshot.docs[snapshot.docs.length - 1];
  }
  return resolved;
}

// The newest completed run of many functions, keyed by function id.
export async function loadLatestCompletedRunsByFunction(companyId, functionIds, options = {}) {
  const wanted = Array.from(new Set((functionIds || []).filter(Boolean)));
  return loadLatestCompletedRunsFor(
    companyId,
    wanted.map((functionId) => ({ key: functionId, functionId })),
    options,
  );
}

// A run is addressed by document id, which needs no index at all.
export async function loadRun(companyId, runId) {
  if (!companyId || !runId) return null;
  const snapshot = await getDoc(doc(db, `users/${companyId}/function_runs/${runId}`));
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data(), _snap: snapshot };
}

// Discover new runs in a small live window, filling any gap back to the last
// processed head. Relevant pending runs get their own document listener until
// compute finishes, so a burst of unrelated runs cannot hide their completion.
// Initial history loading calls observeRuns too, covering older pending runs.
export function watchCardRuns(companyId, targets, onChange, onError) {
  const headSize = 100;
  const subscriptions = new Map();
  const latest = new Map();
  let stopped = false;
  let checkpoint;
  let pendingHead = null;
  let scanning = false;

  const observeRuns = (runs) => {
    if (stopped) return;
    let changed = false;
    for (const run of runs) {
      const matches = targets.filter(target => target.functionId === run.function_id
        && (!target.matches || target.matches(run)));
      if (matches.length === 0) continue;
      const pending = run.status === 'queued' || run.status === 'running';
      if (isCompletedRun(run)) {
        for (const target of matches) {
          const previous = latest.get(target.key);
          if (!previous || runCreatedAt(run) > runCreatedAt(previous)) {
            latest.set(target.key, run);
            changed = true;
          }
        }
      }
      if (!pending) {
        subscriptions.get(run.id)?.unsubscribe?.();
        subscriptions.delete(run.id);
        continue;
      }
      // A pending run older than a result we already know cannot replace it.
      if (matches.every(target => latest.has(target.key)
        && runCreatedAt(run) <= runCreatedAt(latest.get(target.key)))) continue;
      if (subscriptions.has(run.id)) continue;
      const entry = { unsubscribe: null };
      subscriptions.set(run.id, entry);
      entry.unsubscribe = onSnapshot(doc(runsCollection(companyId), run.id), snapshot => {
        if (stopped) return;
        if (snapshot.exists()) observeRuns([mapRun(snapshot)]);
        else {
          entry.unsubscribe?.();
          subscriptions.delete(run.id);
        }
      }, error => {
        if (!stopped) onError(error);
      });
      // Also handle an immediately delivered terminal snapshot.
      if (!subscriptions.has(run.id)) entry.unsubscribe();
    }
    if (changed) onChange([...latest.values()]);
  };

  const fillGap = async () => {
    if (scanning) return;
    scanning = true;
    try {
      while (pendingHead && !stopped) {
        const head = pendingHead;
        pendingHead = null;
        if (checkpoint !== undefined && !head.some(snapshot => snapshot.id === checkpoint?.id)) {
          let after = head[head.length - 1];
          while (!stopped) {
            const page = await getDocsFromServer(query(runsCollection(companyId),
              orderBy('created_at', 'desc'), startAfter(after),
              ...(checkpoint ? [endBefore(checkpoint)] : []), limit(headSize)));
            if (stopped) return;
            observeRuns(page.docs.map(mapRun));
            if (page.docs.length < headSize) break;
            after = page.docs[page.docs.length - 1];
          }
        }
        checkpoint = head[0];
      }
    } catch (error) {
      if (!stopped) onError(error);
    } finally {
      scanning = false;
    }
  };

  const stopHead = onSnapshot(query(runsCollection(companyId),
    orderBy('created_at', 'desc'), limit(headSize)), { includeMetadataChanges: true }, snapshot => {
    if (stopped || snapshot.metadata?.fromCache) return;
    observeRuns(snapshot.docs.map(mapRun));
    if (snapshot.docs.length === 0) {
      if (checkpoint === undefined) checkpoint = null;
      return;
    }
    // Keep only the newest pending head while a gap scan is in progress.
    pendingHead = snapshot.docs;
    fillGap();
  }, error => { if (!stopped) onError(error); });

  return {
    observeRuns,
    unsubscribe: () => {
      stopped = true;
      stopHead();
      subscriptions.forEach(entry => entry.unsubscribe?.());
      subscriptions.clear();
    },
  };
}
